import { convertMultiPolygonToGeometry } from '~/_shared/types/polygon/polygon.utils';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import type { HexColor } from '~/_shared/utils/colors/colors.types';
import type {
  BoundaryCombineGeometryBoundaryRequest, BoundaryCombineGeometryGeometryRequest, BoundaryCombineGeometryRequest,
} from '~/store/boundaries/boundaries.repository';
import type {
  BoundaryGroupId, BoundaryId, BoundaryTerritoryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import {
  type BoundaryStateItems, CUSTOM_BOUNDARY_ZOOM_LEVEL,
} from '~/store/boundaryItems/boundaryItems.state';
import {
  type BoundarySelectState, type BucketColorInfo, type CustomBoundaryQuickEditActiveState,
  isBoundarySelectQuickEditCustomBoundary,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import {
  defaultNextSelectionColorInfo, removeFromAllSelectionColorInfo,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/constants';

type CalculationBasis = Readonly<{
  boundaryGroupId: BoundaryGroupId;
  boundaryIds: readonly BoundaryId[];
}>;

export const buildGeometryCalculations = (calculationBasis: CalculationBasis, selectState: CustomBoundaryQuickEditActiveState): Record<BoundaryTerritoryId, BoundaryCombineGeometryRequest> => {
  const requests = calculationBasis.boundaryIds.reduce((requestsWIP, baseBoundaryId) => {
    const includedBase: BoundaryCombineGeometryBoundaryRequest = {
      type: 'boundary',
      boundary_id: baseBoundaryId,
      boundary_group_id: calculationBasis.boundaryGroupId,
    };

    const additions = selectState.selectedBoundaryInfos
      .filter(({ selectedBucketId }) => selectedBucketId === baseBoundaryId)
      .map(({ boundaryId: addedBoundaryId }) => {
        return {
          type: 'boundary',
          boundary_id: addedBoundaryId,
          boundary_group_id: selectState.selectBoundaryGroupId,
        } as const satisfies BoundaryCombineGeometryBoundaryRequest;
      });

    const removals = selectState.selectedBoundaryInfos
      .filter(({ selectedBucketId }) => selectedBucketId !== baseBoundaryId)
      .map(({ boundaryId: addedBoundaryId }) => {
        return {
          type: 'boundary',
          boundary_id: addedBoundaryId,
          boundary_group_id: selectState.selectBoundaryGroupId,
        } as const satisfies BoundaryCombineGeometryBoundaryRequest;
      });

    if (notEmpty(additions) || notEmpty(removals)) {
      requestsWIP[baseBoundaryId] = {
        include: [includedBase, ...additions],
        subtract: removals,
      };
    }

    return requestsWIP;
  }, {} as Record<BoundaryId, BoundaryCombineGeometryRequest>);

  return requests;
};

export const getSelectionColor = (boundarySelectState: BoundarySelectState, bucketId: number|null): BucketColorInfo => {
  if (isBoundarySelectQuickEditCustomBoundary(boundarySelectState)) {
    const selectedBucketInfo = boundarySelectState.selectionModeData.buckets.find((bucket) => bucket.bucketId === bucketId);
    if (selectedBucketInfo) {
      return selectedBucketInfo.colorInfo;
    }
    return removeFromAllSelectionColorInfo;
  }

  return defaultNextSelectionColorInfo;
};

export const hexColorToBucketColorInfo = (fillColor: HexColor): BucketColorInfo => ({
  fillColor,
  fillOpacity: 1,
  borderColor: '#fff',
  borderOpacity: 0,
  borderWidth: 0,
});

export const convertBoundaryCalculationsToGeometryCalculations = (
  calculations: Record<BoundaryTerritoryId, BoundaryCombineGeometryRequest>,
  boundariesMap: BoundaryStateItems,
): Record<BoundaryTerritoryId, BoundaryCombineGeometryRequest> => {
  return Object.fromEntries(
    Object.entries(calculations).map(([boundaryId, request]) => {
      const convertRequest = (req: BoundaryCombineGeometryRequest['include'][number]): BoundaryCombineGeometryRequest['include'][number] => {
        if (req.type === 'boundary') {
          const boundary = boundariesMap.get(req.boundary_group_id)?.get(req.boundary_id);
          const multiPolygon = boundary?.paths.get(CUSTOM_BOUNDARY_ZOOM_LEVEL);
          if (multiPolygon) {
            return {
              type: 'geometry',
              value: convertMultiPolygonToGeometry(multiPolygon),
            } satisfies BoundaryCombineGeometryGeometryRequest;
          }
        }
        return req;
      };

      const newRequest: BoundaryCombineGeometryRequest = {
        include: request.include.map(convertRequest),
        subtract: request.subtract.map(convertRequest),
      };
      return [boundaryId, newRequest];
    })
  );
};
